import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, viewChild } from '@angular/core';
import { DropdownState, DropdownStore, enterTarget, ExactMatch, SearchFormatter, SearchFunc, showsCreateRow } from './dropdown.store';
import { debounceTime, distinctUntilChanged, EMPTY, fromEvent, merge, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';

// how far the popup keeps from the window's edge, the pages' own padding
const VIEWPORT_MARGIN = 10;

export interface ItemSelectedEvent<T> {
  searchTerm: string;
  item: T | undefined;
}

@Component({
  selector: 'dropdown',
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe
  ],
  // one store per instance — a page may carry several dropdowns at once (one per client position, 0015)
  providers: [DropdownStore],
  host: {
    '[style.left.px]': 'left',
    '[style.top.px]': 'top',
    '[style.opacity]': 'opacity',
    '[style.pointer-events]': 'pointerEvents',
    '(clickOutside)': 'onClickOutside($event)'
  },
  hostDirectives: [
    {
      directive: ClickOutsideDirective,
      outputs: ['clickOutside']
    }
  ]
})
export class DropdownComponent<T> implements OnInit, OnDestroy {
  @Input({ required: true })
  searchFunc!: SearchFunc<T>;

  @Input({ required: true })
  trigger!: HTMLElement;

  @Input()
  searchFormatter: SearchFormatter<T> | undefined;

  // 0 turns the lookup into a static option list: options appear on open,
  // no typing required
  @Input()
  minSearchLength = 3;

  @Input()
  header = 'DODAJ';

  // 300 ms like the list pages' search boxes; 0 for an in-memory option list,
  // where a delay is felt and buys nothing
  @Input()
  debounceMs = 300;

  // a picker that creates (JIT BOM) ends the list with `+ Utwórz „…”`; a pure filter never does (0110)
  @Input()
  allowCreate = false;

  // what "the typed text is this item" means for Enter without a highlight;
  // the label by default, name or part number for a BOM item
  @Input()
  exactMatch: ExactMatch<T> | undefined;

  @Output()
  readonly itemSelected = new EventEmitter<ItemSelectedEvent<T>>();

  @Output()
  readonly closed = new EventEmitter<void>();

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  left = 0;
  top = 0;
  opacity = 0;
  userSelect = 'none';
  pointerEvents: 'none' | 'auto' = 'none';

  readonly state$: Observable<DropdownState<T>>;

  // where the click opened it; left moves off it only to keep the popup inside the window
  private anchorX = 0;
  // the options arrive after open and can widen the popup, so it is placed again on every resize
  private readonly resizeObserver = new ResizeObserver(() => {
    if (this.opacity === 1) {
      this.place();
    }
  });

  // The dropdown stays on the page between openings, so every opening runs its own
  // search: close tears it down with the pending debounce and the request in flight,
  // and a reopened dropdown searches its first term again
  private readonly opened$ = new Subject<boolean>();
  private readonly searchTerm$ = new Subject<string>();

  constructor(
    private store: DropdownStore<T>,
    private destroyRef: DestroyRef,
    private elementRef: ElementRef<HTMLElement>,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init(this.searchFunc, this.searchFormatter, this.minSearchLength, this.exactMatch, this.allowCreate);

    this.opened$
      .pipe(
        switchMap(opened => opened ? this.searchWhileOpen() : EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.inputElement()!.nativeElement.focus();

    this.initOpenListener();

    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.store.destroy();
  }

  open(x: number, y: number): void {
    this.store.open(x, y);
    this.opacity = 1;
    this.anchorX = x;
    this.top = y;
    this.place();
    this.pointerEvents = 'auto';

    this.inputElement()!.nativeElement.focus();

    if (this.minSearchLength === 0) {
      this.store.setSearch('');
    }

    this.opened$.next(true);
  }

  onSearchChange(search: string): void {
    this.store.setSearch(search);
    this.searchTerm$.next(search);
  }

  showsCreateRow = showsCreateRow;

  // Enter takes the highlighted row, an exact match or the create row — never a name
  // the list had not answered yet (0110)
  onEnter(): void {
    const target = enterTarget(this.store.state);

    if (target === 'create') {
      this.onCreateClick();
    } else if (target) {
      this.onItemClick(target.item);
    }
  }

  // the visible create row emits the JIT event: no item, the typed term
  onCreateClick(): void {
    const event: ItemSelectedEvent<T> = {
      item: undefined,
      searchTerm: this.store.state.search!
    };

    this.itemSelected.emit(event);

    this.close();
  }

  // on the input's keydown, not the window's keyup: the caret stays put, a held key repeats
  onArrowDown(event: Event): void {
    event.preventDefault();
    this.store.highlightNext();
  }

  onArrowUp(event: Event): void {
    event.preventDefault();
    this.store.highlightPrevious();
  }

  onEscape(): void {
    if (this.opacity === 0) { // TODO: add open/closed flag
      return;
    }

    this.close();
  }

  onItemClick(item: T): void {
    const event: ItemSelectedEvent<T> = {
      item: item,
      searchTerm: this.store.state.search!
    };

    console.log('DropdownComponent.onItemClick');

    this.itemSelected.emit(event);

    this.close();
  }

  onClickOutside(event: MouseEvent): void {
    if (this.opacity === 0) { // TODO: add open/closed flag
      return;
    }

    // the trigger's own click both opens the dropdown and lands outside its host
    if (this.trigger.contains(event.target as Node)) {
      return;
    }

    this.close();
  }

  private initOpenListener(): void {
    console.log('DropdownComponent.initOpenListener', {
      trigger: this.trigger
    });

    fromEvent<MouseEvent>(this.trigger, 'click')
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(click => {
        // this.store.open(click.clientX, click.clientY);
        this.open(click.clientX, click.clientY);
      });
  }

  // Same rule as the list pages: the fetch waits for typing to settle, and switchMap
  // drops a superseded request so an older answer never overwrites a newer one
  private searchWhileOpen(): Observable<void> {
    // opening is not debounced — a static list shows its options at once
    const initialTerm$ = this.minSearchLength === 0 ? of('') : EMPTY;
    // 0 skips debounceTime entirely, so an in-memory list stays synchronous
    const typedTerm$ = this.debounceMs > 0
      ? this.searchTerm$.pipe(debounceTime(this.debounceMs))
      : this.searchTerm$;

    return merge(initialTerm$, typedTerm$)
      .pipe(
        distinctUntilChanged(),
        switchMap(term => this.store.fetch(term))
      );
  }

  // Opened at the click; a trigger near the right edge (the last button of a toolbar)
  // would push the popup out of the window, so it moves left just enough to fit
  private place(): void {
    const width = this.elementRef.nativeElement.offsetWidth;

    this.left = Math.max(VIEWPORT_MARGIN, Math.min(this.anchorX, window.innerWidth - width - VIEWPORT_MARGIN));
    this.changeDetectorRef.markForCheck();
  }

  private close(): void {
    this.opacity = 0;
    this.pointerEvents = 'none';

    this.opened$.next(false);
    this.store.close();
    this.closed.emit();
  }
}
